import { createSignal, Show, For, onMount } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import { useAuth } from '../lib/auth'
import { getTeamMembers, addTeamMemberByEmail, removeTeamMember,
         getVenueBalance, requestPayout, getPayouts, getVenue } from '../lib/api'

type Tab = 'overview' | 'team' | 'finance' | 'settings'

export default function VenueDashboard() {
  const navigate = useNavigate()
  const auth = useAuth()

  const [tab, setTab] = createSignal<Tab>('overview')
  const [selectedVenueId, setSelectedVenueId] = createSignal<string | null>(null)
  const [venue, setVenue] = createSignal<any>(null)
  const [team, setTeam] = createSignal<Array<{ id: string; email: string; name: string; role: string; joinedAt: string }>>([])
  const [balance, setBalance] = createSignal<{ available: number; pending: number; payoutEnabled: boolean; minimumPayout: number } | null>(null)
  const [payouts, setPayouts] = createSignal<Array<{ id: string; amount: number; fee: number; status: string; requestedAt: string }>>([])
  const [inviteEmail, setInviteEmail] = createSignal('')
  const [inviteRole, setInviteRole] = createSignal('staff')
  const [isProcessing, setIsProcessing] = createSignal(false)

  onMount(async () => {
    if (!auth.isAuthenticated()) {
      navigate('/venue-login')
      return
    }

    // Load first venue
    const venues = auth.user()?.venues ?? []
    if (venues.length > 0) {
      setSelectedVenueId(venues[0].venueId)
      await loadVenueData(venues[0].venueId)
    }
  })

  async function loadVenueData(venueId: string) {
    const vRes = await getVenue(venueId)
    if (vRes.data) setVenue(vRes.data)
    const tRes = await getTeamMembers(venueId)
    if (tRes.data) setTeam(tRes.data.members)
    const bRes = await getVenueBalance(venueId)
    if (bRes.data) setBalance(bRes.data)
    const pRes = await getPayouts(venueId)
    if (pRes.data) setPayouts(pRes.data.payouts)
  }

  async function handleInviteByEmail() {
    if (!inviteEmail() || !selectedVenueId()) return
    setIsProcessing(true)
    const res = await addTeamMemberByEmail(selectedVenueId()!, inviteEmail(), inviteRole())
    if (res.error) {
      alert(res.error.message || 'Failed to invite member')
    } else {
      setInviteEmail('')
      await loadVenueData(selectedVenueId()!)
    }
    setIsProcessing(false)
  }

  async function handleRemoveMember(memberId: string) {
    if (!selectedVenueId()) return
    setIsProcessing(true)
    await removeTeamMember(selectedVenueId()!, memberId)
    await loadVenueData(selectedVenueId()!)
    setIsProcessing(false)
  }

  async function handleRequestPayout() {
    if (!selectedVenueId()) return
    setIsProcessing(true)
    await requestPayout(selectedVenueId()!)
    await loadVenueData(selectedVenueId()!)
    setIsProcessing(false)
  }

  const venues = () => auth.user()?.venues ?? []
  const activeVenue = () => venues().find(v => v.venueId === selectedVenueId())

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      {/* Header */}
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-2xl mx-auto flex items-center justify-between">
          <div class="flex items-center gap-3">
            <img src="/logo.svg" alt="ZipCheck" class="h-7" />
            <div>
              <span class="font-semibold text-gray-900">ZipCheck</span>
              <span class="ml-2 text-xs text-gray-500">Venue Dashboard</span>
            </div>
          </div>

          {/* Venue switcher */}
          <Show when={venues().length > 1}>
            <select
              value={selectedVenueId() ?? ''}
              onChange={(e) => {
                setSelectedVenueId(e.currentTarget.value)
                loadVenueData(e.currentTarget.value)
              }}
              class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <For each={venues()}>
                {(v) => <option value={v.venueId}>{v.venueName}</option>}
              </For>
            </select>
          </Show>

          <button onClick={() => auth.logout()}
            class="text-sm text-gray-500 hover:text-gray-700">
            Sign out
          </button>
        </div>
      </header>

      {/* Tab bar */}
      <div class="bg-white border-b border-gray-200">
        <div class="max-w-2xl mx-auto px-4 flex gap-1 overflow-x-auto">
          <For each={[
            ['overview', 'Overview'],
            ['team', 'Team'],
            ['finance', 'Finance'],
            ['settings', 'Settings'],
          ] as const}>
            {([key, label]) => (
              <button
                onClick={() => setTab(key)}
                class={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  tab() === key
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            )}
          </For>
        </div>
      </div>

      <main class="flex-1 p-4">
        <div class="max-w-2xl mx-auto">
          {/* Venue name */}
          <h1 class="text-2xl font-bold text-gray-900 mb-6">
            {activeVenue()?.venueName || 'Venue Dashboard'}
          </h1>

          {/* ─── Overview Tab ──────────────────────── */}
          <Show when={tab() === 'overview'}>
            <div class="grid grid-cols-2 gap-4 mb-6">
              <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <p class="text-sm text-gray-500 mb-1">Available balance</p>
                <p class="text-2xl font-bold text-gray-900">
                  €{((balance()?.available ?? 0) / 100).toFixed(2)}
                </p>
              </div>
              <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <p class="text-sm text-gray-500 mb-1">Pending</p>
                <p class="text-2xl font-bold text-gray-900">
                  €{((balance()?.pending ?? 0) / 100).toFixed(2)}
                </p>
              </div>
            </div>

            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4">
              <h2 class="text-sm font-semibold text-gray-700 mb-3">Quick actions</h2>
              <div class="grid grid-cols-3 gap-2">
                <button onClick={() => setTab('team')}
                  class="py-3 px-4 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200">
                  Team
                </button>
                <button onClick={() => setTab('finance')}
                  class="py-3 px-4 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200">
                  Payouts
                </button>
                <button onClick={() => setTab('settings')}
                  class="py-3 px-4 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200">
                  Settings
                </button>
              </div>
            </div>

            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h2 class="text-sm font-semibold text-gray-700 mb-3">Venue info</h2>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-500">Venue ID</span>
                  <span class="font-mono text-gray-700">{selectedVenueId()}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Your role</span>
                  <span class="capitalize text-gray-700">{activeVenue()?.role}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Team members</span>
                  <span class="text-gray-700">{team().length}</span>
                </div>
              </div>
            </div>
          </Show>

          {/* ─── Team Tab ─────────────────────────── */}
          <Show when={tab() === 'team'}>
            {/* Add staff by email */}
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4">
              <h2 class="text-sm font-semibold text-gray-700 mb-3">Add staff member</h2>
              <div class="flex gap-2">
                <input
                  type="email"
                  placeholder="staff@example.com"
                  value={inviteEmail()}
                  onInput={(e) => setInviteEmail(e.currentTarget.value)}
                  class="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm
                         focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <select
                  value={inviteRole()}
                  onChange={(e) => setInviteRole(e.currentTarget.value)}
                  class="px-3 py-3 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                </select>
                <button
                  onClick={handleInviteByEmail}
                  disabled={isProcessing() || !inviteEmail()}
                  class="py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg text-sm
                         hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  Invite
                </button>
              </div>
              <p class="mt-2 text-xs text-gray-500">
                They'll receive an email to join. If they don't have a ZipCheck account, they'll create one.
              </p>
            </div>

            {/* Team list */}
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h2 class="text-sm font-semibold text-gray-700 mb-3">Team ({team().length})</h2>
              <div class="space-y-2">
                <For each={team()}>
                  {(member) => (
                    <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span class="text-sm font-bold text-emerald-700">
                            {(member.name || member.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p class="text-sm font-medium text-gray-900">{member.name || member.email}</p>
                          <p class="text-xs text-gray-500 capitalize">{member.role}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        class="text-xs text-red-500 hover:text-red-700 px-3 py-1"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </For>
              </div>
            </div>
          </Show>

          {/* ─── Finance Tab ──────────────────────── */}
          <Show when={tab() === 'finance'}>
            {/* Balance + Payout */}
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4">
              <p class="text-sm text-gray-500 mb-1">Available for payout</p>
              <p class="text-3xl font-bold text-gray-900 mb-4">
                €{((balance()?.available ?? 0) / 100).toFixed(2)}
              </p>

              <button
                onClick={handleRequestPayout}
                disabled={isProcessing() || !(balance()?.payoutEnabled)}
                class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                       hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {isProcessing() ? 'Processing...' : 'Request payout'}
              </button>

              <Show when={!(balance()?.payoutEnabled)}>
                <p class="mt-2 text-xs text-gray-500 text-center">
                  Minimum payout: €{((balance()?.minimumPayout ?? 2000) / 100).toFixed(2)}
                </p>
              </Show>

              <p class="mt-2 text-xs text-gray-400 text-center">
                Payout fees (€0.25) are deducted from the payout amount.
              </p>
            </div>

            {/* Payout history */}
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h2 class="text-sm font-semibold text-gray-700 mb-3">Payout history</h2>
              <Show
                when={payouts().length > 0}
                fallback={<p class="text-sm text-gray-400">No payouts yet.</p>}
              >
                <div class="space-y-2">
                  <For each={payouts()}>
                    {(p) => (
                      <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p class="text-sm font-medium text-gray-900">€{((p.amount) / 100).toFixed(2)}</p>
                          <p class="text-xs text-gray-500">Fee: €{((p.fee) / 100).toFixed(2)}</p>
                          <p class="text-xs text-gray-400">
                            {new Date(p.requestedAt).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric',
                            })}
                          </p>
                        </div>
                        <span class={`text-xs px-2 py-0.5 rounded-full ${
                          p.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          p.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {p.status}
                        </span>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </Show>

          {/* ─── Settings Tab ─────────────────────── */}
          <Show when={tab() === 'settings'}>
            <div class="space-y-4">
              {/* Venue Profile */}
              <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 class="text-sm font-semibold text-gray-700 mb-3">Venue profile</h2>
                <div class="space-y-3">
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Venue name</label>
                    <input type="text" value={venue()?.name || ''} placeholder="Club Matrix Berlin"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Description</label>
                    <textarea rows={3} placeholder="Berlin's premier nightclub wardrobe service"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Address</label>
                    <input type="text" placeholder="Friedrichstraße 123, Berlin"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                </div>
                <button class="mt-4 py-2 px-4 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700">
                  Save changes
                </button>
              </div>

              {/* Services / Products */}
              <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 class="text-sm font-semibold text-gray-700 mb-3">Services & pricing</h2>
                <div class="space-y-2">
                  <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p class="text-sm font-medium text-gray-900">Item</p>
                      <p class="text-xs text-gray-500">Default product</p>
                    </div>
                    <div class="flex items-center gap-2">
                      <input type="number" value="0.99" step="0.01" min="0.99"
                        class="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right" />
                      <span class="text-sm text-gray-500">€</span>
                    </div>
                  </div>
                </div>
                <button class="mt-3 text-sm text-emerald-600 hover:text-emerald-700">
                  + Add service
                </button>
              </div>

              {/* QR Code */}
              <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 class="text-sm font-semibold text-gray-700 mb-3">Venue QR code</h2>
                <div class="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <p class="text-xs text-gray-400">QR Code</p>
                </div>
                <div class="flex gap-2 justify-center">
                  <button class="py-2 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
                    Download PNG
                  </button>
                  <button class="py-2 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
                    Download PDF
                  </button>
                </div>
              </div>

              {/* Operating Hours */}
              <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 class="text-sm font-semibold text-gray-700 mb-3">Operating hours</h2>
                <div class="space-y-2">
                  <For each={['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']}>
                    {(day) => (
                      <div class="flex items-center justify-between">
                        <span class="text-sm text-gray-700 w-24">{day}</span>
                        <div class="flex items-center gap-2">
                          <input type="time" value="18:00" class="px-2 py-1 border border-gray-300 rounded text-sm" />
                          <span class="text-gray-400">—</span>
                          <input type="time" value="04:00" class="px-2 py-1 border border-gray-300 rounded text-sm" />
                        </div>
                      </div>
                    )}
                  </For>
                </div>
                <button class="mt-4 py-2 px-4 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700">
                  Save hours
                </button>
              </div>
            </div>
          </Show>
        </div>
      </main>
    </div>
  )
}
